import React, { useState, useEffect } from 'react';
import {
  getAllUsers,
  formatDate,
  filterChurnedUsers,
  exportToCSV,
  loadUserAvatarCache,
  loadUserStatsCache,
  refreshUserAvatars,
  refreshUserStats,
} from '../userService';
import UserDetail from './UserDetail';
import '../styles/users.css';
import '../styles/icon-clues.css';

const PAGE_SIZE = 500;

function UserAvatar({ photoURL, name }) {
  if (photoURL) {
    const fullResURL = photoURL.replace(/=s\d+-c$/, '=s2000-c');
    return (
      <a href={fullResURL} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
        <img className="user-avatar" src={photoURL} alt="" referrerPolicy="no-referrer" />
      </a>
    );
  }
  const initial = (name || '?')[0].toUpperCase();
  return <span className="user-avatar-placeholder">{initial}</span>;
}

const SUBSCRIPTION_LABELS = { paid: 'Plus', trial: 'Trial', promo: 'Promo', lifetime: 'Lifetime' };

// Sort order: paying, trial, promo, lapsed, never subscribed
function subscriptionRank(sub) {
  if (!sub) return 0;
  if (!sub.active) return 1;
  return { promo: 2, trial: 3, paid: 4, lifetime: 4 }[sub.kind] || 2;
}

function SubscriptionBadge({ sub }) {
  if (!sub) return null;
  const expires = sub.expiresMs
    ? new Date(sub.expiresMs).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;
  if (!sub.active) {
    return <span className="sub-badge sub-badge-expired" title={expires ? `Expired ${expires}` : undefined}>Expired</span>;
  }
  const notes = [
    expires && `${sub.willRenew ? 'Renews' : 'Ends'} ${expires}`,
    sub.billingIssue && 'billing issue',
    sub.sandbox && 'sandbox',
  ].filter(Boolean).join(', ');
  return (
    <span className={`sub-badge sub-badge-${sub.kind}${sub.willRenew || sub.kind !== 'paid' ? '' : ' sub-badge-ending'}`} title={notes}>
      {SUBSCRIPTION_LABELS[sub.kind] || 'Plus'}{sub.billingIssue ? ' !' : ''}
    </span>
  );
}

function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [page, setPage] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [avatarCache, setAvatarCache] = useState({});
  const [statsCache, setStatsCache] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [subscribersOnly, setSubscribersOnly] = useState(false);

  useEffect(() => {
    loadUsers();
    loadCaches();
  }, []);

  useEffect(() => {
    filterAndSortUsers();
  }, [users, searchTerm, sortConfig, subscribersOnly, statsCache]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, sortConfig, subscribersOnly]);

  async function loadUsers() {
    setLoading(true);
    try {
      const allUsers = await getAllUsers();
      setUsers(allUsers);
    } catch (error) {
      console.error('Error loading users:', error);
      alert('Failed to load users');
    } finally {
      setLoading(false);
    }
  }

  async function loadCaches() {
    const [avatars, stats] = await Promise.all([
      loadUserAvatarCache(),
      loadUserStatsCache(),
    ]);
    setAvatarCache(avatars);
    setStatsCache(stats);
  }

  // Stats, subscriptions and avatars in one go; the two functions run side by side
  async function handleRefreshStats() {
    setLoadingStats(true);
    try {
      const [statsResult, avatarsResult] = await Promise.allSettled([refreshUserStats(), refreshUserAvatars()]);
      const [stats, avatars] = await Promise.all([loadUserStatsCache(), loadUserAvatarCache()]);
      setStatsCache(stats);
      setAvatarCache(avatars);
      const failed = [statsResult, avatarsResult].filter(r => r.status === 'rejected');
      failed.forEach(r => console.error('Error refreshing users:', r.reason));
      if (failed.length) alert('Some of the refresh failed, see the console');
      else if (statsResult.value?.subscriptionLookupsFailed) {
        alert(`${statsResult.value.subscriptionLookupsFailed} RevenueCat lookups failed, subscription info may be incomplete`);
      }
    } finally {
      setLoadingStats(false);
    }
  }

  function subscriptionOf(userId) {
    return statsCache?.subscriptions?.[userId] || null;
  }

  async function handleExportChurned() {
    if (!statsCache) {
      const confirmed = window.confirm(
        'This requires detailed stats. Click "Refresh" first, then try again. Load stats now?'
      );
      if (!confirmed) return;
      await handleRefreshStats();
      return;
    }

    const usersWithStats = users.map(u => ({
      ...u,
      totalCrosswords: statsCache.stats?.[u.id]?.totalCrosswords || 0,
      totalWordle: statsCache.stats?.[u.id]?.totalWordle || 0,
    }));

    const churnedUsers = filterChurnedUsers(usersWithStats);

    if (churnedUsers.length === 0) {
      alert('No churned users found matching criteria.');
      return;
    }

    const confirmExport = window.confirm(
      `Found ${churnedUsers.length} churned users (registered 5+ days ago, 0-1 crosswords & wordle). Export to CSV?`
    );
    if (confirmExport) {
      exportToCSV(churnedUsers, 'churned-users.csv');
      alert(`Exported ${churnedUsers.length} churned users to churned-users.csv`);
    }
  }

  function filterAndSortUsers() {
    let result = [...users];

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(
        user =>
          user.name.toLowerCase().includes(lowerSearch) ||
          user.email.toLowerCase().includes(lowerSearch)
      );
    }

    if (subscribersOnly) {
      result = result.filter(user => subscriptionOf(user.id)?.active);
    }

    result.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      if (sortConfig.key === 'createdAt' || sortConfig.key === 'lastActive') {
        aVal = aVal?.toMillis ? aVal.toMillis() : (typeof aVal === 'number' ? aVal : 0);
        bVal = bVal?.toMillis ? bVal.toMillis() : (typeof bVal === 'number' ? bVal : 0);
      }

      if (sortConfig.key === 'totalCrosswords' || sortConfig.key === 'totalWordle') {
        aVal = statsCache?.stats?.[a.id]?.[sortConfig.key] ?? -1;
        bVal = statsCache?.stats?.[b.id]?.[sortConfig.key] ?? -1;
      }

      if (sortConfig.key === 'subscription') {
        aVal = subscriptionRank(subscriptionOf(a.id));
        bVal = subscriptionRank(subscriptionOf(b.id));
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredUsers(result);
  }

  function handleSort(key) {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }

  function getSortIndicator(key) {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  }

  if (loading) {
    return <div className="users-panel"><p>Loading users...</p></div>;
  }

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const pageUsers = filteredUsers.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  function goToPage(newPage) {
    setPage(newPage);
    window.scrollTo(0, 0);
  }

  const pagination = totalPages > 1 && (
    <div className="ic-pagination">
      <button className="btn-secondary" onClick={() => goToPage(0)} disabled={currentPage === 0}>«</button>
      <button className="btn-secondary" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 0}>‹ Prev</button>
      <span className="ic-page-info">
        Page {currentPage + 1} / {totalPages} ({currentPage * PAGE_SIZE + 1}–{Math.min((currentPage + 1) * PAGE_SIZE, filteredUsers.length)})
      </span>
      <button className="btn-secondary" onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= totalPages - 1}>Next ›</button>
      <button className="btn-secondary" onClick={() => goToPage(totalPages - 1)} disabled={currentPage >= totalPages - 1}>»</button>
    </div>
  );

  const hasStats = !!statsCache;
  const hasSubscriptions = !!statsCache?.subscriptions;
  const subscriberCount = hasSubscriptions
    ? users.filter(u => statsCache.subscriptions[u.id]?.active).length
    : 0;
  const statsLastUpdated = statsCache?.lastUpdated
    ? new Date(statsCache.lastUpdated).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;

  return (
    <div className="users-panel">
      <div className="users-header">
        <h1>
          Users ({filteredUsers.length})
          {hasSubscriptions && <span className="users-subscriber-count">{subscriberCount} with Plus</span>}
        </h1>
        <div className="users-header-controls">
          <button
            className="btn-secondary"
            onClick={handleRefreshStats}
            disabled={loadingStats}
            title="Fetch game stats, subscriptions (RevenueCat) and profile pictures for all users"
          >
            {loadingStats ? 'Refreshing...' : hasStats ? `Refresh (${statsLastUpdated})` : 'Refresh'}
          </button>
          {hasSubscriptions && (
            <label className="users-filter-toggle">
              <input
                type="checkbox"
                checked={subscribersOnly}
                onChange={(e) => setSubscribersOnly(e.target.checked)}
              />
              Subscribers only
            </label>
          )}
          <button
            className="btn-secondary export-churned-button"
            onClick={handleExportChurned}
            disabled={loadingStats}
          >
            Export Churned Users
          </button>
          <input
            type="text"
            className="users-search"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {pagination}

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('name')}>
                Name{getSortIndicator('name')}
              </th>
              <th onClick={() => handleSort('email')}>
                Email{getSortIndicator('email')}
              </th>
              <th onClick={() => handleSort('createdAt')}>
                Signup Date{getSortIndicator('createdAt')}
              </th>
              {hasSubscriptions && (
                <th onClick={() => handleSort('subscription')}>
                  Plus{getSortIndicator('subscription')}
                </th>
              )}
              <th onClick={() => handleSort('streakCount')}>
                Streak{getSortIndicator('streakCount')}
              </th>
              {hasStats && (
                <>
                  <th onClick={() => handleSort('totalCrosswords')}>
                    Crosswords{getSortIndicator('totalCrosswords')}
                  </th>
                  <th onClick={() => handleSort('totalWordle')}>
                    Wordle{getSortIndicator('totalWordle')}
                  </th>
                </>
              )}
              <th onClick={() => handleSort('lastActive')}>
                Last Active{getSortIndicator('lastActive')}
              </th>
            </tr>
          </thead>
          <tbody>
            {pageUsers.map(user => (
              <tr key={user.id} onClick={() => setSelectedUserId(user.id)}>
                <td>
                  <div className="user-name-cell">
                    <UserAvatar photoURL={avatarCache[user.id]} name={user.name} />
                    {user.name}
                  </div>
                </td>
                <td>{user.email}</td>
                <td>{formatDate(user.createdAt)}</td>
                {hasSubscriptions && <td><SubscriptionBadge sub={subscriptionOf(user.id)} /></td>}
                <td>{user.streakCount}</td>
                {hasStats && (
                  <>
                    <td>{statsCache.stats?.[user.id]?.totalCrosswords ?? 0}</td>
                    <td>{statsCache.stats?.[user.id]?.totalWordle ?? 0}</td>
                  </>
                )}
                <td>{formatDate(user.lastActive)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination}

      {selectedUserId && (
        <UserDetail
          userId={selectedUserId}
          photoURL={avatarCache[selectedUserId]}
          onClose={() => setSelectedUserId(null)}
        />
      )}
    </div>
  );
}

export default UsersPanel;
